import type { Kitchen, KitchenSection, PapercutCategory, ProductUnavailability } from '../../api/products/types';
import type { ProductCategoryOptions } from './categories';

export type ProductStatus = 'active' | 'inactive' | 'draft';

export type ProductPublishedStatus = 'published' | 'unpublished';
export const PRODUCT_PUBLISHED_STATUS_VALUES = ['published', 'unpublished'] as const;

export type ProductForm = 'buffet' | 'mini_buffet' | 'gathering_box' | 'meal_box' | 'treat_box';
export const PRODUCT_FORM_VALUES = ['buffet', 'mini_buffet', 'gathering_box', 'meal_box', 'treat_box'] as const;

// GRAIN-2473: formats a user may pick when creating or editing an event.
// Meal Box / Treat Box / Gathering Box are hidden from the event format
// dropdown and folded under Mini Buffet, but they remain valid Product forms
// (products, and preset menus, can still use them), so PRODUCT_FORM_VALUES
// above is left intact for product management, preset menus, and label lookups.
export const EVENT_FORM_VALUES: ProductForm[] = ['buffet', 'mini_buffet'];

// GRAIN-3510: Gathering Box becomes a selectable event format only when the
// `gathering_box` feature flag is on; otherwise it stays folded under Mini
// Buffet (GRAIN-2473). Callers read the flag via useFeatureFlag and pass the
// boolean so this stays a pure, testable helper.
export function eventFormValues(gatheringBoxEnabled: boolean): ProductForm[] {
  return gatheringBoxEnabled ? [...EVENT_FORM_VALUES, 'gathering_box'] : EVENT_FORM_VALUES;
}

// GRAIN-2473: Mini Buffet is an umbrella — a Mini Buffet event's menu picker
// may select products of any of these forms. Mirrors Product::MINI_BUFFET_FORMS.
export const MINI_BUFFET_FORMS: ProductForm[] = ['mini_buffet', 'gathering_box', 'meal_box', 'treat_box'];
export const MACROS_FORMATS: ProductForm[] = ['meal_box', 'treat_box'];
export const PORTION_FORMATS: ProductForm[] = ['buffet', 'mini_buffet', 'gathering_box'];
export const DEFAULT_FORM_LABELS: Record<ProductForm, string> = {
  buffet: 'Buffet',
  mini_buffet: 'Mini Buffet',
  gathering_box: 'Gathering Box',
  meal_box: 'Meal Box',
  treat_box: 'Treat Box',
};

/**
 * Maps a Product::Forms "format" to its event_type. Mirrors
 * FulfilmentEvent::FORM_EVENT_TYPES on the backend: buffet stays buffet,
 * gathering_box is its own event_type (GRAIN-3461), and the remaining box/mini
 * formats resolve to mini_buffet.
 */
export function formToEventType(form: string): 'buffet' | 'mini_buffet' | 'gathering_box' {
  if (form === 'buffet') return 'buffet';
  if (form === 'gathering_box') return 'gathering_box';
  return 'mini_buffet';
}

export interface ProductMacros {
  calories: number | null;
  carbohydrates: number | null;
  fats: number | null;
  protein: number | null;
}

export interface ProductImage {
  signed_id: string;
  url: string;
  filename: string;
}

// The uploaded display-copy file (a PDF), not an image.
export interface ProductAttachment {
  signed_id: string;
  url: string;
  filename: string;
}

export interface ProductInventoryItem {
  id: number;
  name: string;
  category: string | null;
  quantity: number | null;
  image_url: string | null;
}

export interface ProductContainerItem {
  id: number;
  name: string;
  category: string | null;
  capacity: number;
  minimum_pax_required: number | null;
  image_url: string | null;
}

export interface ProductStockItem {
  id: number;
  quantity: number;
  start_date: string | null;
  end_date: string | null;
}

export interface Product {
  id: number;
  name: string;
  status: ProductStatus;
  active_status: 'active' | 'inactive';
  category: string | null;
  form: ProductForm;
  // Papercut kitchen metadata. Blank on imported/section-less products — the
  // backend only requires these for grain-authored products that sync to
  // Papercut, so the UI must render them gracefully absent.
  kitchen: Kitchen | null;
  kitchen_section: KitchenSection[];
  papercut_category: PapercutCategory | null;
  serving_temperature: string | null;
  cost_to_company: number | null;
  recommended_retail_price: number | null;
  price_excluding_gst: number | null;
  price_including_gst: number | null;
  archived_at: string | null;
  catalogues: string[];
  active_period_start: string | null;
  active_period_end: string | null;
  // Server-computed for the list's active_on date; absent otherwise.
  unavailability?: ProductUnavailability | null;
  food_cost: number | null;
  food_cost_percent: number | null;
  allergen_tags: string[];
  nutrition_tags: string[];
  chef_recommended: boolean;
  bcrs: boolean;
  display_unit: string;
  ingredients: string | null;
  serving_size: string | null;
  key_serving_info: string | null;
  chinese_name: string | null;
  production_notes: string | null;
  fulfilment_notes: string | null;
  byline: string | null;
  story: string | null;
  stocks_start: string | null;
  stocks_end: string | null;
  macros: ProductMacros | null;
  diet_tags: string[];
  images: ProductImage[];
  display_attachment: ProductAttachment | null;
  inventory_items: ProductInventoryItem[];
  container_items: ProductContainerItem[];
  stock_items: ProductStockItem[];
  created_at: string;
}

export interface ProductTab {
  key: ProductPublishedStatus;
  label: string;
}

// Translation interfaces
export interface ProductTabsTranslations {
  published: string;
  unpublished: string;
}

export interface ProductBulkBarTranslations {
  products_selected: string;
  products_publishing: string;
  products_queued: string;
  publish: string;
  dismiss: string;
  confirm_title: string;
  confirm_message: string;
  confirm_action: string;
  confirm_cancel: string;
}

export interface ProductToolbarTranslations {
  filters?: string;
  new_product?: string;
}

export interface ProductTableTranslations {
  columns?: Record<string, string>;
  empty?: string;
}

export interface FiltersModalTranslations {
  title?: string;
  select_all?: string;
  clear_all?: string;
  selected?: string;
  apply?: string;
  close?: string;
  categories_label?: string;
  status?: string;
  catalogues?: string;
  categories?: string;
  tags?: string;
  status_plural?: string;
  catalogues_plural?: string;
  categories_plural?: string;
  tags_plural?: string;
  format?: string;
  format_plural?: string;
  active?: string;
  inactive?: string;
}

export interface ProductTranslations {
  tabs?: ProductTabsTranslations;
  bulk_bar?: ProductBulkBarTranslations;
  header?: { title?: string; total_product?: string };
  search?: { placeholder?: string; aria_label?: string };
  toolbar?: ProductToolbarTranslations;
  table?: ProductTableTranslations;
  filters?: FiltersModalTranslations;
}

// Component props
export interface ProductsTabsProps {
  currentStatus: ProductPublishedStatus;
  onStatusChange: (status: ProductPublishedStatus) => void;
  disabled?: boolean;
  layoutId?: string;
  className?: string;
}

export interface ProductsToolbarProps {
  onFiltersClick?: () => void;
  onNewProductClick?: () => void;
  /** Count of active committed filters. When > 0, a badge renders on the Filters button. */
  filterCount?: number;
  /** RBAC: hide the New Product button for read-only users. Defaults to false (fail closed). */
  canWrite?: boolean;
  className?: string;
}

export interface ProductFilterDismissHandlers {
  removeFormat: (form: ProductForm) => void;
  removeStatus: (status: string) => void;
  removeCatalogue: (id: number) => void;
  removeCategory: (category: string) => void;
  removeDietTag: (tag: string) => void;
  removeAllergenTag: (tag: string) => void;
  removeNutritionTag: (tag: string) => void;
  removeChefPick: () => void;
  removeServingTemperature: (temp: string) => void;
}

/** Committed filter values (as stored in the URL), used for rendering chips. */
export interface CommittedProductFilters {
  /** Any-of format filter. Empty means every format. */
  formats: ProductForm[];
  /** Active / inactive — the Filters modal's Status tab. */
  statuses: string[];
  catalogueIds: number[];
  categories: string[];
  dietTags: string[];
  allergenTags: string[];
  nutritionTags: string[];
  chefPick: boolean;
  servingTemperatures: string[];
}

export type FilterCategory = 'format' | 'status' | 'catalogues' | 'categories' | 'tags';
// Sidebar order in the Filters modal. Format leads: it is the filter people
// reach for most, and it is the one that changes the table's columns.
export const FILTER_CATEGORIES: FilterCategory[] = ['format', 'status', 'catalogues', 'categories', 'tags'] as const;

export interface FiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters?: import('@hooks/products').ProductFilters;
  children?: React.ReactNode;
  selectedCount?: number;
  categoryCounts?: Partial<Record<FilterCategory, number>>;
  isLoading?: boolean;
}

export interface ProductsTableProps {
  products: Product[];
  /** Drives which column set renders — see `ProductsTable`'s column memo. */
  currentFormats: ProductForm[];
  currentStatus: ProductPublishedStatus;
  isLoading?: boolean;
  isFetching?: boolean;
  onRowClick?: (product: Product) => void;
  sort?: string;
  direction?: 'asc' | 'desc' | '';
  onSortChange?: (sort: string, direction: 'asc' | 'desc' | '') => void;
  /** RBAC: when false, hides row write-actions, the selection checkboxes, and
   * the bulk-action overlay. Defaults to false (fail closed). */
  canWrite?: boolean;
  className?: string;
}

export interface AllProductsPageProps {
  currentStatus: ProductPublishedStatus;
  totalCount: number;
  onStatusChange: (status: ProductPublishedStatus) => void;
  isLoading: boolean;
  isFetching: boolean;
  products: Product[];
  sort?: string;
  direction?: 'asc' | 'desc' | '';
  onSortChange?: (sort: string, direction: 'asc' | 'desc' | '') => void;
  onRowClick?: (product: Product) => void;
  onNewProductClick?: () => void;
  filters: import('@hooks/products').ProductFilters;
  filterCount: number;
  committedFilters: CommittedProductFilters;
  dismissHandlers: ProductFilterDismissHandlers;
  /** RBAC: gates the New Product button + all table write-affordances.
   * Defaults to false (fail closed). */
  canWrite?: boolean;
  /** Server-defined category vocabulary — feeds the filter chips. */
  categoryOptions: ProductCategoryOptions;
}
